import { defineStore } from 'pinia'
import { $http } from '@/composables'

// Types
export interface FeedbackChoice {
  id: number
  rating: number
  choice_text: string
  display_order: number
  is_active: boolean
  created_at: string
}

export interface TicketFeedback {
  id: number
  ticket_id: number
  customer_id: number | null
  triggered_by_user_id: number
  rating: number | null
  comment: string | null
  status: 'pending' | 'submitted'
  created_at: string
  updated_at: string
  choices_by_rating: Record<string, FeedbackChoice[]>
  selections?: number[]
}

export interface SubmitFeedbackRequest {
  rating: number
  choice_ids?: number[]
  comment?: string
}

export const useFeedbackStore = defineStore('feedback', {
  state: () => ({
    currentFeedback: null as TicketFeedback | null,
    loading: false,
    error: null as string | null,
  }),

  getters: {
    /**
     * Feedback durumunu kontrol et
     */
    isPending: (state) => state.currentFeedback?.status === 'pending',

    /**
     * Feedback gönderilmiş mi?
     */
    isSubmitted: (state) => state.currentFeedback?.status === 'submitted',

    /**
     * Seçili rating için choice'ları getir
     */
    getChoicesByRating: (state) => (rating: number) => {
      if (!state.currentFeedback?.choices_by_rating) return []
      return state.currentFeedback.choices_by_rating[rating.toString()] || []
    },

    /**
     * Tüm rating seviyeleri
     */
    allRatings: (state) => {
      if (!state.currentFeedback?.choices_by_rating) return []
      return Object.keys(state.currentFeedback.choices_by_rating)
        .map(Number)
        .sort((a, b) => a - b)
    },

    /**
     * Mevcut rating
     */
    currentRating: (state) => state.currentFeedback?.rating || null,

    /**
     * Seçili choice'lar
     */
    selectedChoices: (state) => state.currentFeedback?.selections || [],

    /**
     * Yorum
     */
    comment: (state) => state.currentFeedback?.comment || null,

    /**
     * Feedback var mı?
     */
    hasFeedback: (state) => state.currentFeedback !== null,
  },

  actions: {
    /**
     * Ticket feedback'ini getir
     * GET /tickets/{ticket_id}/feedback
     */
    async getFeedback(ticketId: number): Promise<TicketFeedback | null> {
      this.loading = true
      this.error = null
      try {
        const response: any = await $http.get(`tickets/${ticketId}/feedback`)
        this.currentFeedback = response.payload.feedback
        return this.currentFeedback
      } catch (error: any) {
        this.error = error.message
        this.currentFeedback = null
        return null
      } finally {
        this.loading = false
      }
    },

    /**
     * Ticket feedback'i gönder
     * POST /tickets/{ticket_id}/feedback/submit
     */
    async submitFeedback(
      ticketId: number,
      data: SubmitFeedbackRequest,
    ): Promise<TicketFeedback | null> {
      // Validation
      if (!data.rating || data.rating < 1 || data.rating > 5) {
        this.error = 'Rating 1 ile 5 arasında olmalıdır'
        return null
      }

      if (
        (!data.choice_ids || data.choice_ids.length === 0) &&
        (!data.comment || data.comment.trim() === '')
      ) {
        this.error = 'En az bir seçenek veya yorum girmelisiniz'
        return null
      }

      this.loading = true
      this.error = null
      try {
        const response: any = await $http.post(`tickets/${ticketId}/feedback/submit`, data)
        this.currentFeedback = response.payload.feedback
        return this.currentFeedback
      } catch (error: any) {
        this.error = error.message || 'Feedback gönderilemedi'
        return null
      } finally {
        this.loading = false
      }
    },

    /**
     * Feedback durumunu kontrol et ve yükle
     */
    async checkFeedbackStatus(ticketId: number): Promise<'pending' | 'submitted' | 'not_found'> {
      const feedback = await this.getFeedback(ticketId)
      if (!feedback) return 'not_found'
      return feedback.status
    },

    /**
     * State'i temizle
     */
    clearState() {
      this.currentFeedback = null
      this.loading = false
      this.error = null
    },

    /**
     * Sadece error'u temizle
     */
    clearError() {
      this.error = null
    },
  },
})
